import React, { useMemo, useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import AccountPicker from './ScraperV2AccountPicker';

/**
 * Add scraper-tagged accounts to a live session. Raises Total/Available and
 * spawns matching crawl workers on the running driver.
 */
export default function ScraperV2AddAccountsPanel({
  sessionId,
  sessionStatus,
  sessionAccountIds = [],
  accounts = [],
  onAdded,
}) {
  const [open, setOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const inSession = useMemo(
    () => new Set((sessionAccountIds || []).map((id) => String(id))),
    [sessionAccountIds]
  );

  const available = useMemo(
    () => (accounts || []).filter((acc) => {
      if (inSession.has(String(acc._id))) return false;
      if (acc.hasValidSession === false) return false;
      return true;
    }),
    [accounts, inSession]
  );

  const canAdd = ['running', 'paused', 'stopped'].includes(sessionStatus);

  const handleSubmit = async () => {
    if (!selectedIds.length || !sessionId || submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/scraper-v2/sessions/${sessionId}/add-accounts`, {
        accountIds: selectedIds,
      });
      const added = res.data?.added || 0;
      if (added > 0) {
        toast.success(
          `Added ${added} scrape account${added === 1 ? '' : 's'} — concurrency raised to ${res.data.totalAccounts}`
        );
      } else {
        toast(res.data?.message || 'No new accounts added');
      }
      setSelectedIds([]);
      setOpen(false);
      onAdded?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add scrape accounts');
    } finally {
      setSubmitting(false);
    }
  };

  if (!canAdd) return null;

  return (
    <div className="rounded-xl border border-slate-200/60 dark:border-slate-700/60 bg-white/60 dark:bg-slate-900/40 p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            Add scrape accounts
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Accounts with the scraper tag — raises live concurrency
            {available.length ? ` · ${available.length} available` : ''}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-600 px-2.5 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          {open ? 'Hide' : 'Add'}
        </button>
      </div>

      {open && (
        <div className="mt-3 space-y-3">
          {available.length === 0 ? (
            <p className="text-sm text-slate-500">
              No more scraper-tagged accounts with a valid session outside this job.
            </p>
          ) : (
            <>
              <AccountPicker
                title="Scrape accounts to add"
                accounts={available}
                selectedIds={selectedIds}
                onChange={setSelectedIds}
                selectableFilter={(acc) => acc.hasValidSession !== false}
              />
              <button
                type="button"
                disabled={!selectedIds.length || submitting}
                onClick={handleSubmit}
                className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
              >
                <PlusIcon className="h-4 w-4" />
                {submitting
                  ? 'Adding…'
                  : `Add ${selectedIds.length || ''} account${selectedIds.length === 1 ? '' : 's'}`.trim()}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
